import type { LayerKey } from '@festina-life/api'
import { dateUtils, notEmpty, type Optional } from '@festina-life/core'
import dayjs from 'dayjs'
import { useCallback } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { getGlMapOperationFromDate } from '../../uiModels/glMapOperations/helpers'
import type { GlMapOperation } from '../../uiModels/glMapOperations/types'
import { useGlMapsForBlockAndLayer } from '../../uiModels/glMapOperations/useGlMapsForBlockAndLayer'
import { stringToDateTimeNullSafe } from '../../uiModels/helpers'
import { useIsViewDateActiveForOperation } from './useIsViewDateActiveForOperation'

const glMapOperationInterval = (operation: GlMapOperation) => ({
  from: stringToDateTimeNullSafe(
    getGlMapOperationFromDate(operation),
    Number.NEGATIVE_INFINITY,
  ),
})

/**
 * Tells whether a GL-map operation would be in scope at the current view date, and moves
 * the view date onto it when it would not, so that a freshly created operation can be
 * viewed right away.
 */
export const useManageGlMapDraftViewDate = ({
  layer,
  block,
}: {
  layer: LayerKey
  block: string
}) => {
  const { onViewAtChanged } = useConfigurationContext()
  const glMapsForBlockAndLayer = useGlMapsForBlockAndLayer({ layer, block })
  const operationsForMappingId = useCallback(
    (mappingId: Optional<string>) =>
      mappingId
        ? glMapsForBlockAndLayer
            .get(mappingId)
            ?.operations.map(({ internalOperation }) => internalOperation)
        : undefined,
    [glMapsForBlockAndLayer],
  )
  const checkIsViewDateActiveForGlMapOperation =
    useIsViewDateActiveForOperation<GlMapOperation>({
      operationInterval: glMapOperationInterval,
    })

  const isViewDateActiveFor = useCallback(
    (fromDate: Optional<string>, mappingId: Optional<string>) =>
      checkIsViewDateActiveForGlMapOperation(
        fromDate,
        operationsForMappingId(mappingId),
      ),
    [checkIsViewDateActiveForGlMapOperation, operationsForMappingId],
  )

  const moveViewDateOnto = useCallback(
    (fromDate: Optional<string>, mappingId: string) => {
      if (isViewDateActiveFor(fromDate, mappingId)) {
        return
      }

      const firstOperationDate = (operationsForMappingId(mappingId) ?? [])
        .map((operation) => getGlMapOperationFromDate(operation))
        .find(notEmpty)

      onViewAtChanged(
        fromDate
          ? dateUtils.dateUtcAsLocal(fromDate).toDate()
          : // Given that if there's no from date and the operation is not in scope, there
            // must be a version with a from date between the beginning of time and the view date.
            dayjs(firstOperationDate!).subtract(1, 'day').toDate(),
      )
    },
    [isViewDateActiveFor, operationsForMappingId, onViewAtChanged],
  )

  return { isViewDateActiveFor, moveViewDateOnto }
}
